import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';


const LeaveApplicationForm = ({ onSubmit }) => {
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [formData, setFormData] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    reason: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const leaveTypeOptions = [
    { value: 'vacation', label: 'Vacation Leave' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'personal', label: 'Personal Leave' },
    { value: 'emergency', label: 'Emergency Leave' },
    { value: 'maternity', label: 'Maternity Leave' },
    { value: 'paternity', label: 'Paternity Leave' }
  ];

  useEffect(() => {
    loadEmployeeData();
    loadLeaveBalance();
  }, [user]);

  const loadEmployeeData = async () => {
    if (!user) return;
    
    try {
      const { data: employeeData, error } = await supabase?.from('employees')?.select('id, employee_id')?.eq('user_id', user?.id)?.single();
      
      if (error) throw error;
      setEmployee(employeeData);
    } catch (error) {
      console.error('Failed to load employee data:', error);
    }
  };

  const loadLeaveBalance = async () => {
    if (!user) return;
    
    try {
      const { data: employeeData, error: empError } = await supabase?.from('employees')?.select('id')?.eq('user_id', user?.id)?.single();
      
      if (empError) throw empError;
      
      if (employeeData) {
        const currentYear = new Date()?.getFullYear();
        const currentMonth = new Date()?.getMonth() + 1;
        
        const { data: balanceData, error: balanceError } = await supabase?.from('leave_balances')?.select('closing_balance')?.eq('employee_id', employeeData?.id)?.eq('year', currentYear)?.eq('month', currentMonth)?.single();
        
        if (balanceError && balanceError?.code !== 'PGRST116') {
          throw balanceError;
        }
        
        setLeaveBalance(balanceData?.closing_balance || 0);
      }
    } catch (error) {
      console.error('Failed to load leave balance:', error);
      setLeaveBalance(0);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.leaveType) newErrors.leaveType = 'Leave type is required';
    if (!formData?.startDate) newErrors.startDate = 'Start date is required';
    if (!formData?.endDate) newErrors.endDate = 'End date is required';
    if (!formData?.reason?.trim()) newErrors.reason = 'Reason is required';
    
    if (formData?.startDate && formData?.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const calculateDays = () => {
    if (formData?.startDate && formData?.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm() || !employee) return;

    // Check if leave balance is 0
    if (leaveBalance === 0) {
      setErrors({ submit: 'Cannot apply for leave. Your available leave balance is 0 days.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const totalDays = calculateDays();
      
      const { error } = await supabase?.from('leave_requests')?.insert({
          employee_id: employee?.id,
          leave_type: formData?.leaveType,
          start_date: formData?.startDate,
          end_date: formData?.endDate,
          reason: formData?.reason,
          total_days: totalDays,
          leave_status: 'pending'
        });

      if (error) throw error;

      setShowSuccess(true);
      setFormData({
        leaveType: '',
        startDate: '',
        endDate: '',
        reason: ''
      });

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

      if (onSubmit) {
        onSubmit(formData);
      }

    } catch (error) {
      console.error('Error submitting leave request:', error);
      setErrors({ submit: error?.message || 'Failed to submit leave request' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Apply for Leave</h2>
        {leaveBalance !== null && (
          <div className="text-sm text-muted-foreground">
            Available Balance: <span className="font-semibold text-primary">{leaveBalance} days</span>
          </div>
        )}
      </div>
      {/* Success Message */}
      {showSuccess && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <p className="text-sm font-medium text-success">Leave request submitted successfully!</p>
          </div>
        </div>
      )}
      {/* Error Message */}
      {errors?.submit && (
        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <p className="text-sm font-medium text-error">{errors?.submit}</p>
          </div>
        </div>
      )}
      {/* Leave Balance Information */}
      <div className="mb-6 p-4 bg-primary/5 border border-primary/10 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <Icon name="Info" size={16} color="var(--color-primary)" />
          <p className="text-sm font-medium text-primary">Leave Balance Information</p>
        </div>
        <p className="text-sm text-muted-foreground">
          You earn 1.5 leave days per month when you maintain full attendance without taking any leave. 
          Unused leave carries forward to the next month and is eligible year over year.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Leave Type <span className="text-error">*</span>
            </label>
            <select
              value={formData?.leaveType}
              onChange={(e) => handleInputChange('leaveType', e?.target?.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring ${
                errors?.leaveType 
                  ? 'border-error focus:ring-error' : 'border-border focus:ring-ring'
              }`}
            >
              <option value="">Select leave type</option>
              {leaveTypeOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
            {errors?.leaveType && (
              <p className="text-sm text-error mt-1">{errors?.leaveType}</p>
            )}
          </div>

          <div className="md:col-span-1">
            {calculateDays() > 0 && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} color="var(--color-accent)" />
                  <span className="text-sm font-medium text-accent">
                    Duration: {calculateDays()} day{calculateDays() !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Start Date <span className="text-error">*</span>
            </label>
            <input
              type="date"
              value={formData?.startDate}
              onChange={(e) => handleInputChange('startDate', e?.target?.value)}
              min={new Date()?.toISOString()?.split('T')?.[0]}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring ${
                errors?.startDate 
                  ? 'border-error focus:ring-error' : 'border-border focus:ring-ring'
              }`}
            />
            {errors?.startDate && (
              <p className="text-sm text-error mt-1">{errors?.startDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              End Date <span className="text-error">*</span>
            </label>
            <input
              type="date"
              value={formData?.endDate}
              onChange={(e) => handleInputChange('endDate', e?.target?.value)}
              min={formData?.startDate || new Date()?.toISOString()?.split('T')?.[0]}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring ${
                errors?.endDate 
                  ? 'border-error focus:ring-error' : 'border-border focus:ring-ring'
              }`}
            />
            {errors?.endDate && (
              <p className="text-sm text-error mt-1">{errors?.endDate}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Reason for Leave <span className="text-error">*</span>
          </label>
          <textarea
            value={formData?.reason}
            onChange={(e) => handleInputChange('reason', e?.target?.value)}
            rows={4}
            placeholder="Please provide a detailed reason for your leave request..."
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none ${
              errors?.reason 
                ? 'border-error focus:ring-error' : 'border-border focus:ring-ring'
            }`}
          />
          {errors?.reason && (
            <p className="text-sm text-error mt-1">{errors?.reason}</p>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFormData({
                leaveType: '',
                startDate: '',
                endDate: '',
                reason: ''
              });
              setErrors({});
            }}
            className="w-full sm:w-auto"
          >
            Clear Form
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || leaveBalance === 0}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Submit Leave Request'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LeaveApplicationForm;